import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import logo3 from "../assets/logo3.svg";
import axios from "axios";
import ContactUsDialog from "./ContactUsDialog";

function Copyright() {
  return (
    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
      {"Copyright © "}
      <Link color="text.secondary" href="https://mui.com/">
        Zenthra Technologies
      </Link>
      &nbsp;
      {new Date().getFullYear()}
    </Typography>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);

  const handleContactDialogClose = () => {
    setContactDialogOpen(false);
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  const LogoImg = styled("img")(({ theme }) => ({
    width: "130px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  }));

  const downloadHandbook = async () => {
    try {
      const response = await axios.post(
        "/downloadHandbook",
        { email },
        { responseType: "blob" }
      );

      if (response.status === 200) {
        // Handle successful file download
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "handbook.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setToast({
          open: true,
          message: "Handbook downloaded successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      if (err.response && err.response.data instanceof Blob) {
        // Convert Blob to JSON
        err.response.data.text().then((text) => {
          try {
            const json = JSON.parse(text);
            setToast({
              open: true,
              message: json.message || "Something went wrong!",
              severity: json.severity || "error",
            });
            setEmail("");
          } catch (parseError) {
            setToast({
              open: true,
              message: "An unexpected error occurred!",
              severity: "error",
            });
            setEmail("");
          }
        });
      } else {
        setToast({
          open: true,
          message: "Network error or server not responding",
          severity: "error",
        });
        setEmail("");
      }
    }
  };

  return (
    <Container
      maxWidth
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "aliceblue",
        alignItems: "center",
        gap: { xs: 4, sm: 8 },
        py: { xs: 8, sm: 10 },
        textAlign: { sm: "center", md: "left" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          width: "100%",
          justifyContent: "space-between",
          paddingX: { xs: 4, sm: 8 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minWidth: { xs: "100%", sm: "60%" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "60%" } }}>
            <Link to="/">
              <LogoImg src={logo3} alt="Logo" />
            </Link>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ fontWeight: 600, mt: 2 }}
            >
              Download our handbook
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              (For employees only)
            </Typography>
            <InputLabel htmlFor="email-newsletter">Email</InputLabel>
            <Stack direction="row" spacing={1} useFlexGap>
              <TextField
                id="email-newsletter"
                type="email"
                value={email}
                hiddenLabel
                size="small"
                variant="outlined"
                fullWidth
                aria-label="Enter your email address"
                placeholder="Your email address"
                onChange={(e) => setEmail(e.target.value)}
                slotProps={{
                  htmlInput: {
                    autoComplete: "off",
                    "aria-label": "Enter your email address",
                  },
                }}
                sx={{ width: "250px" }}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={downloadHandbook}
              >
                Download
              </Button>
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            Zenthra
          </Typography>
          <Link color="text.secondary" variant="body2" href="/">
            <Typography>Home</Typography>
          </Link>
          <Link color="text.secondary" variant="body2" href="/aboutus">
            <Typography>About us</Typography>
          </Link>
          <Link
            color="text.secondary"
            variant="body2"
            style={{ cursor: "pointer" }}
          >
            <Typography onClick={() => setContactDialogOpen(true)}>
              Contact
            </Typography>
          </Link>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          pt: { xs: 4, sm: 8 },
          width: "100%",
          borderTop: "1px solid",
          borderColor: "divider",
          paddingX: { xs: 4, sm: 8 },
        }}
      >
        <div>
          <Link color="text.secondary" variant="body2" href="#">
            Privacy Policy
          </Link>
          <Typography sx={{ display: "inline", mx: 0.5, opacity: 0.5 }}>
            &nbsp;•&nbsp;
          </Typography>
          <Link color="text.secondary" variant="body2" href="#">
            Terms of Service
          </Link>
          <Copyright />
        </div>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }} // Moves it to the top-right
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor:
              toast.severity === "success" ? "#4CAF50" : "#D32F2F", // Green for success, red for error
            color: "#fff",
            fontSize: "1rem",
            fontWeight: "bold",
            padding: "12px 24px",
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toast.severity}
          sx={{
            width: "100%",
            fontSize: "1.1rem",
            fontWeight: 600,
            padding: "10px 20px",
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      <ContactUsDialog
        open={contactDialogOpen}
        onClose={handleContactDialogClose}
      />
    </Container>
  );
}
